import { Toast } from "@tento-ui/components/toast/toast";
import { Tracing } from "@tento-core/tracing";
import { InstanceUrl } from "@tento-core/instance-url";
import {
  NotPromotableError,
  PromoteIncompleteError,
  PromoteWriteError,
  SdkRequestError,
} from "@tento-lona";

/**
 * The app's one surface for an SDK write the server refused.
 *
 * The SDK's required writes (`tasks.create/update/delete`,
 * `events.create/update/delete/moveTo/copyTo`, `rows.add/delete/move`,
 * `sheets.create/delete/reorder`, `setCell`, custom-cell writes, the
 * copy-on-write promote) REJECT with a typed error after rolling back
 * whatever they applied optimistically — so the caller's only job is to say
 * so. A fire-and-forget call site must still attach this, or a transient
 * server hiccup becomes an unhandled rejection:
 *
 *     void sdk.tasks.update(task, attrs, actor).catch((e: unknown) =>
 *       reportWriteError("update the task", e)
 *     );
 *
 * `action` is the verb phrase the toast completes — "Couldn't <action>".
 */
export function reportWriteError(action: string, e: unknown): void {
  const message = e instanceof Error ? e.message : String(e);
  Tracing.warn("net")(`write failed (${action}): ${message}`);
  const { title, description } = describeWriteError(action, e);
  Toast.error(title, description != null ? { description } : null);
}

/**
 * The app's `ClientConfig.requestErrorHandler`: a tolerant SDK read that failed
 * (a cell range, which then renders empty) is traced, and a lapsed session
 * goes to sign-in — the same rule `LonaApi.delegate` applies to the app's
 * own requests.
 */
export function onSdkRequestError(e: SdkRequestError): void {
  Tracing.warn("net")(`sdk tolerant request failed: ${e.message}`);
  if (e.reason === "not_logged_in") {
    window.location.href = InstanceUrl.root("/signin");
  }
}

/** The toast `reportWriteError` shows for `e`: typed SDK errors first. */
export function describeWriteError(
  action: string,
  e: unknown,
): { title: string; description: Option<string> } {
  if (e instanceof NotPromotableError) {
    return e.persistence === "synced"
      ? { title: "This sheet is already saved", description: null }
      : {
          title: "This sheet can't be saved",
          description: "It's a local sheet with no way to save it to your account.",
        };
  }
  if (e instanceof PromoteIncompleteError) {
    const missing = e.missingRowKeys.length;
    return {
      title: "Couldn't save this sheet",
      description:
        `${missing} of ${e.localRows} rows didn't save, so the sheet stays ` +
        "local. Try again.",
    };
  }
  if (e instanceof PromoteWriteError) {
    return {
      title: "Couldn't save this sheet",
      description: serverSentence(e),
    };
  }
  // One base across the SDK: the rows `RowsHttpBackendRequestError` extends it too.
  if (e instanceof SdkRequestError) {
    return {
      title: `Couldn't ${action}`,
      description: serverSentence(e),
    };
  }
  return {
    title: `Couldn't ${action}`,
    description: e instanceof Error ? e.message : String(e),
  };
}

/**
 * The server's own sentence, else its reason slug, else what became of the
 * request (`SdkRequestError.outcome`): refused, unanswered (it may still
 * have saved), never sent, or failed in the app's own code.
 */
function serverSentence(e: SdkRequestError): string {
  if (e.details.length > 0) return e.details;
  if (e.reason != null) return e.reason.replaceAll("_", " ");
  switch (e.outcome) {
    case "refused":
      return `The server refused the change (${e.status ?? "no status"}).`;
    case "unanswered":
      return "The server didn't answer, so the change may not have saved. " +
        "Check your connection and try again.";
    case "malformed":
      return "The server's answer couldn't be read, so the change may have " +
        "saved. Reload to check before trying again.";
    case "unaddressable":
      return "The change couldn't be sent.";
    case "faulted":
      return "Something went wrong in the app. Reload and try again.";
  }
}
